import React from "react";

import ManageChats from "@editor/components/editor/ai/chat/ManageChats";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { trpc } from "@editor/utils/trpc";

import { CANVAS_DATA } from "@/features/canvas/canvas-constants";
import ReactEditorChat from "@/features/react-editor/components/chat/ReactEditorChat";
// import DesignLibraryPanel from "@/features/react-editor/components/DesignLibraryPanel";
import ModifiablePageRenderer from "@/features/react-editor/components/ModifiablePageRenderer";
import { ModifiersProvider } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import ReactEditorGlobalHotkeysListener from "@/features/react-editor/components/ReactEditorGlobalHotkeysListener";
import { useViewportStore } from "@/features/react-editor/store/viewportStore";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { Navigate, useSearchParams } from "react-router-dom";

import {
  ChatProvider,
  useChatStoreActions,
  useViewingVersionId,
} from "./components/chat/store/ChatStoreContext";
import DesignLibraryPanel from "./components/DesignLibraryPanel";
import VersionHistory from "./components/history/VersionHistory";
import LoadingHeader from "./components/LoadingHeader";
import PageRenderControls from "./components/PageRenderControls";
import PageSettingsPanel from "./components/PageSettingsPanel";
import { usePanelView } from "./store/editorStore";

function ReactEditor() {
  const isReaplo = isFeatureEnabled("reaplo");
  const { project } = useCurrentProjectContext();
  const projectId = useCurrentProjectId();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const panelView = usePanelView();

  // Create a ref for the main container to handle keyboard events
  const reactEditorContainerRef = React.useRef<HTMLDivElement>(null);

  const { data: page } = trpc.nextJSProject.getPage.useQuery(
    projectId ? { projectId, id: pageId ?? "" } : skipToken,
    {
      enabled: Boolean(projectId && pageId),
    },
  );

  if (!project) {
    return null;
  }

  if (!isReaplo) {
    return <Navigate to={`/projects/${project.id}`} replace />;
  }

  return (
    <ChatProvider>
      <ReactEditorContent
        pageId={pageId}
        panelView={panelView}
        reactEditorContainerRef={reactEditorContainerRef}
        page={page}
      />
    </ChatProvider>
  );
}

function ReactEditorContent({
  pageId,
  panelView,
  reactEditorContainerRef,
  page,
}: {
  pageId: string | null;
  panelView: string;
  reactEditorContainerRef: React.RefObject<HTMLDivElement>;
  page: any;
}) {
  const projectId = useCurrentProjectId();
  const viewingVersionId = useViewingVersionId();
  const { setViewingVersionId } = useChatStoreActions();
  const currentViewport = useViewportStore((state) => state.currentViewport);

  const { data: fileVersionsData } = trpc.fileVersion.findByFileId.useQuery(
    projectId && pageId
      ? {
          projectId,
          fileId: pageId,
          cursor: 0,
          limit: 1,
        }
      : skipToken,
  );

  const fileVersions = fileVersionsData?.fileVersions;

  if (fileVersions && fileVersions.length > 0 && viewingVersionId === null) {
    setViewingVersionId(fileVersions[0]?.id ?? null);
  }

  return (
    <ModifiersProvider>
      {/* NOTE (Kurt, 2025-07-17): We need to create a ref for the main container to handle keyboard events. Without
      this, hotkeys don't work because the events are captured by iframes or other elements.*/}
      <div
        ref={reactEditorContainerRef}
        className="flex gap-3 h-full w-full outline-none"
        tabIndex={-1}
      >
        <ReactEditorGlobalHotkeysListener
          containerRef={reactEditorContainerRef}
        />
        <div
          className={twMerge(
            "flex w-full h-full flex-col gap-5  min-h-0 items-center  ",
            pageId && "w-1/3",
          )}
        >
          {panelView === "chat" && <ReactEditorChat />}
          {panelView === "history" && <VersionHistory />}
          {panelView === "designLibrary" && <DesignLibraryPanel />}
          {panelView === "pageSettings" && page && (
            <PageSettingsPanel page={page} />
          )}
          {panelView === "manageChats" && <ManageChats />}
        </div>

        {pageId && (
          <div className="relative flex-1 min-h-0 flex flex-col items-center w-full shadow-selectable border-0.5 border-border rounded-lg bg-white">
            {page && <PageRenderControls page={page} />}
            <div className=" top-0 left-0 w-full relative">
              <LoadingHeader />
            </div>

            <div
              className="flex-1 relative w-full"
              style={{
                maxWidth:
                  currentViewport !== "desktop"
                    ? `${CANVAS_DATA[currentViewport].defaultFrameWidth}px`
                    : undefined,
              }}
            >
              {page && (
                <div className="flex flex-col h-full w-full">
                  <ModifiablePageRenderer
                    pageRoute={page.pageRoute ?? ""}
                    className="rounded-t-none w-full"
                    isInteractive
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </ModifiersProvider>
  );
}

export default ReactEditor;
